var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { BehaviorSubject, Subject, from, of, merge, zip, combineLatest, timer } from 'rxjs';
import { delay, filter, distinctUntilChanged, shareReplay, takeUntil, map, switchMap, mapTo, take, debounce, } from 'rxjs/operators';
import * as R from 'ramda';
import { FormValidator } from './form-validator';
import { findElementWith, findElementBy, mapFormValuesFlatten, applyFormChangesAsMutable } from '../utils/def.utils';
import { ElementDefType } from '../../models/def.types';
import { FormElementChangedBy, FormulaChangeProperty, } from '../../models/formula.types';
import { mapValidationFrame } from '../utils/validation.utils';
import { mapFormDefFlatten } from '../utils/_.utils';
import { FormCalculatorContext } from './form-calculator';
var current = null;
// all errors -> touched
var highlightAllErrors = R.pipe(R.toPairs, R.map(function (_a) {
    var id = _a[0];
    return [id, true];
}), R.fromPairs);
var FormContext = /** @class */ (function () {
    function FormContext(config) {
        var _this = this;
        this.config = config;
        // destroy emitter
        this.destroy$ = new Subject();
        // context state
        this.state$ = new BehaviorSubject({ errors: {}, touched: {}, values: {} });
        // changes
        this.changes$ = new BehaviorSubject({});
        this.asyncChanges$ = new BehaviorSubject(new Map());
        // form errors
        this.errors$ = this.state$.pipe(map(R.pick(['errors', 'touched'])), delay(0), filter(function () { return !_this.validator.isValidating; }), distinctUntilChanged(R.equals), takeUntil(this.destroy$), shareReplay(1));
        // form definition
        this._definition$ = new BehaviorSubject(null);
        this._definition$Map = new Map();
        this._definitionsMap = {};
        this.definition$ = this._definition$
            .asObservable()
            .pipe(delay(0), takeUntil(this.destroy$), shareReplay(1));
        this.subscribeOnChanges = function (obs, sub) {
            obs
                .pipe(delay(0), filter(function (c) { return !!c && !R.isEmpty(c); }), debounce(function (c) { return timer(c[FormContext.CHANGE_IMMEDIATELY] ? 2 : FormContext.CHANGE_DEBOUNCE_TIME); }), takeUntil(_this.destroy$))
                .subscribe(sub);
        };
        // check uniq of change
        this.isUniqPropertyChange = function (id, key, value) {
            var hasChanges = R.hasPath([id, key], _this.changes);
            var eqChange = R.pathEq([id, key], value);
            if ((hasChanges && eqChange(_this.changes)) || eqChange(_this._definitionsMap)) {
                return false;
            }
            switch (key) {
                case FormulaChangeProperty.VALUE:
                    return hasChanges || !R.propEq(id, value, _this.values);
                case 'touched':
                    return !R.propEq(id, value, _this.touched);
                default:
                    return true;
            }
        };
        // get form flatten values
        this.mapFormValues = function (definition) {
            if (definition === void 0) { definition = _this.formDefinition; }
            return mapFormValuesFlatten(definition);
        };
        this.calcFormDefinition = function (skipOnChanges, cb) {
            if (skipOnChanges === void 0) { skipOnChanges = false; }
            // calc form def
            var next = _this.calulator.calcFormDefinition();
            var apply = function () {
                if (!skipOnChanges || R.isEmpty(_this.changes)) {
                    _this.formDefinition = next;
                    // set next values
                    _this.state$.next(__assign(__assign({}, _this.state), { values: _this.mapFormValues() }));
                    if (cb) {
                        cb();
                    }
                }
            };
            skipOnChanges ? setImmediate(apply) : apply();
        };
        // after changes observable
        this.afterChanges$ = function () {
            return combineLatest([
                _this.asyncChanges$.pipe(filter(function (c) { return !c.size; })),
                _this.changes$.pipe(filter(function (c) { return !c || R.isEmpty(c); })),
            ]).pipe(take(1), mapTo(true));
        };
        // after changes promise
        this.afterChanges = function () {
            return _this.afterChanges$().toPromise(Promise);
        };
        // after validation promise
        this.afterValidation = function (element) { return __awaiter(_this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.validator
                            .afterValidation$()
                            .pipe(take(1))
                            .toPromise(Promise)];
                    case 1:
                        _a.sent();
                        return [2 /*return*/, element ? this.isElementValid(element) : this.isFormValid()];
                }
            });
        }); };
        // init form context with new definition
        this.initWithDefinition = function (definition) {
            if (!R.equals(definition, _this.originDefinition)) {
                _this.originDefinition = definition;
                // create form formulas definition
                _this.calulator.createFormulaDefinition(definition);
                // calc form
                _this.calcFormDefinition();
                // update form validation frame & validate
                _this.updateValidationFrame(mapValidationFrame(_this.formDefinition));
                _this.validate();
                return true;
            }
            return false;
        };
        // apply form changes
        this.setChanges = function (changes) {
            // apply changes
            applyFormChangesAsMutable(_this.calulator.formulaDefinition, changes);
            // recalc form
            _this.calcFormDefinition(true, function () {
                // form validation
                if (_this.config.validateOnChange) {
                    _this.validate();
                }
                // apply touched changes
                _this.setTouchedByChanges(changes);
            });
        };
        // set form elements touched by changes
        this.setTouchedByChanges = function (changes) {
            return setImmediate(function () {
                var touchedPairs = Object.entries(changes)
                    .filter(R.hasPath([1, 'touched']))
                    .map(function (_a) {
                    var id = _a[0], touched = _a[1].touched;
                    return [id, touched];
                });
                if (touchedPairs.length) {
                    _this.setTouched(__assign(__assign({}, _this.touched), R.fromPairs(touchedPairs)));
                }
            });
        };
        // set form elements touched
        this.setTouched = function (touched) {
            _this.state$.next(__assign(__assign({}, _this.state), { touched: touched }));
        };
        // set form errors
        this.setErrors = function (errors) {
            var prevErrors = _this.errors;
            _this.state$.next(__assign(__assign({}, _this.state), { errors: errors }));
            setImmediate(function () {
                if (!R.equals(errors, prevErrors)) {
                    // recalc on new errors
                    _this.calcFormDefinition(true);
                    // reset block validation errors
                    _this.blockValidationErrors = null;
                }
            });
        };
        // element error as observable
        this.elementError$ = function (field) {
            return _this.errors$.pipe(map(function (errors) { return ({
                error: R.path(['errors', field], errors) || null,
                touched: R.path(['touched', field], errors) || false,
            }); }));
        };
        this.blockValidationErrors = null;
        // element async change
        this.handleAsyncPropertyChange = R.curry(function (id, key, value) {
            var changeKey = FormContext.mapAsyncChangeKey(id, key);
            var next = new Map(_this.asyncChanges);
            next.set(changeKey, from(value));
            _this.asyncChanges$.next(next);
        });
        // mark change as sync, to cancel async change
        this.markPropertyChangeAsSync = R.curry(function (id, key) {
            var changeKey = FormContext.mapAsyncChangeKey(id, key);
            if (_this.asyncChanges.get(changeKey)) {
                var next = new Map(_this.asyncChanges);
                next.delete(changeKey);
                _this.asyncChanges$.next(next);
            }
        });
        // element change
        this.handlePropertyChange = R.curry(function (id, key, value, immediatly) {
            var _a, _b, _c;
            // push change
            if (_this.isUniqPropertyChange(id, key, value)) {
                var changes = R.mergeDeepRight(_this.changes, (_a = {},
                    _a[id] = key === FormulaChangeProperty.ACTIVE_INDEX ? { frontOptions: (_b = {}, _b[key] = value, _b) } : (_c = {}, _c[key] = value, _c),
                    _a[FormContext.CHANGE_IMMEDIATELY] = immediatly,
                    _a));
                _this.changes$.next(changes);
            }
            // cancel async
            if (key !== 'touched') {
                _this.markPropertyChangeAsSync(id, key);
            }
        });
        // element touch
        this.handleTouched = R.curry(function (id, touched, immediatly) {
            if (immediatly === void 0) { immediatly = false; }
            _this.handlePropertyChange(id, 'touched', touched, immediatly);
        });
        // element value change
        this.handleChange = R.curry(function (id, value, immediatly) {
            if (immediatly === void 0) { immediatly = false; }
            _this.handlePropertyChange(id, FormulaChangeProperty.VALUE, value, immediatly);
        });
        // element blur
        this.handleBlur = R.curry(function (id, evt) {
            if (_this.config.validateOnBlur) {
                _this.validate();
            }
        });
        this.handleActiveIndexChange = R.curry(function (id, activeIndex) {
            _this.handlePropertyChange(id, FormulaChangeProperty.ACTIVE_INDEX, activeIndex, true);
        });
        // update form validator frame
        this.updateValidationFrame = function (frame) {
            _this.validator.updateFrame(frame);
        };
        // init validator
        this.validator = new FormValidator(this);
        // init calculator
        this.calulator = new FormCalculatorContext(this);
        // form changes with debounce
        this.subscribeOnChanges(this.changes$, function (changes) {
            _this.setChanges(changes);
            _this.changes$.next({});
        });
        // async form changes
        this.asyncChanges$
            .pipe(delay(0), switchMap(function (c) { return merge.apply(void 0, Array.from(c.entries()).map(function (_a) {
            var k = _a[0], v = _a[1];
            return zip(of(k), v);
        })); }), filter(function (c) { return !!c; }), takeUntil(this.destroy$))
            .subscribe(function (_a) {
            var changeKey = _a[0], value = _a[1];
            // push change
            return _this.handlePropertyChange.apply(_this, __spreadArrays(FormContext.formatAsyncChangeKey(changeKey), [value, false]));
        });
        //
        current = this;
    }
    FormContext.getCurrent = function () {
        return current;
    };
    Object.defineProperty(FormContext.prototype, "state", {
        get: function () {
            return this.state$.getValue();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "changes", {
        // form changes in the moment
        get: function () {
            return this.changes$.getValue() || {};
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "asyncChanges", {
        // form async changes in the moment
        get: function () {
            return this.asyncChanges$.getValue();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "isValidating", {
        // is validation in progress
        get: function () {
            return this.validator.isValidating;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "errors", {
        // form elements errors
        get: function () {
            return this.state.errors;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "touched", {
        // form touched elements
        get: function () {
            return this.state.touched;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "values", {
        // form flatten values
        get: function () {
            return this.state.values;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "formDefinition", {
        // current form definition
        get: function () {
            return this._definition$.getValue();
        },
        // set next form definition
        set: function (val) {
            var _this = this;
            this._definition$.next(val);
            if (val) {
                this._definitionsMap = mapFormDefFlatten(function (def) {
                    var definition$ = _this._definition$Map.get(def.id);
                    if (!definition$) {
                        definition$ = new BehaviorSubject(def);
                        _this._definition$Map.set(def.id, definition$);
                    }
                    if (definition$.getValue().lastChanged < def.lastChanged) {
                        definition$.next(def);
                    }
                    return def;
                })(val);
            }
            else {
                this._definition$Map.clear();
            }
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(FormContext.prototype, "formulaDefinition", {
        // current  form definition for formulas
        get: function () {
            return this.findFormulaDefinition();
        },
        enumerable: true,
        configurable: true
    });
    //
    FormContext.prototype.getElementDefinition = function (id) {
        return this._definitionsMap[id];
    };
    FormContext.prototype.getElementDefinition$ = function (id) {
        var definition$ = this._definition$Map.get(id);
        if (!definition$) {
            definition$ = new BehaviorSubject(this.getElementDefinition(id));
            this._definition$Map.set(id, definition$);
        }
        return definition$.asObservable().pipe(takeUntil(this.destroy$));
    };
    // is element value changed in the moment
    FormContext.prototype.isElementValueChanged = function (element) {
        var id = element.id, value = element.value;
        if (R.equals(this.values[id], value)) {
            return false;
        }
        else if (R.hasPath([id, FormulaChangeProperty.VALUE], this.changes) && R.equals(this.changes[id].value, value)) {
            return FormElementChangedBy.USER;
        }
        return FormElementChangedBy.FORMULA;
    };
    // is element value fetching
    FormContext.prototype.isElementValueFetching = function (element) {
        return !!this.asyncChanges.get(FormContext.mapAsyncChangeKey(element.id, FormulaChangeProperty.VALUE));
    };
    // is element touched
    FormContext.prototype.isElementTouched = function (element) {
        var touched = this.touched[element.id] || R.path([element.id, 'touched'], this.changes);
        if (!touched && element.$type === ElementDefType.BLOCK) {
            return !!element.elements.find(this.isElementTouched.bind(this));
        }
        return !!touched;
    };
    // search form formula element definition
    FormContext.prototype.findFormulaDefinition = function (id) {
        return !id
            ? this.calulator.formulaDefinition
            : findElementBy('id', id, this.calulator.formulaDefinition);
    };
    FormContext.prototype.findFormulaDefinitionBy = function (prop, value) {
        return findElementBy(prop, value, this.calulator.formulaDefinition);
    };
    // set touched all invalid elements
    FormContext.prototype.highlightErrors = function (element) {
        var _this = this;
        var highlight = element
            ? mapFormDefFlatten(function (_a) {
                var id = _a.id;
                return (_this.errors[id] ? true : undefined);
            })(element)
            : highlightAllErrors(this.errors);
        this.setTouched(__assign(__assign({}, this.touched), highlight));
        // store block validation errors in form context, while they are present
        if (element) {
            var mapBlockErrors_1 = {};
            Object.keys(highlight).forEach(function (id) { return id in _this.errors && (mapBlockErrors_1[id] = _this.errors[id]); });
            this.blockValidationErrors = mapBlockErrors_1;
        }
    };
    // elements changes
    FormContext.prototype.handleChanges = function (payload, target, immediatly) {
        var _this = this;
        if (immediatly === void 0) { immediatly = false; }
        if (payload) {
            var id_1 = target ? target.id : null;
            switch (payload.$kind) {
                case 'changes':
                    if (payload.changes) {
                        payload.changes.forEach(function (it) {
                            return _this.handlePropertyChange(it.id || id_1, it.$kind || FormulaChangeProperty.VALUE, it.value, immediatly);
                        });
                    }
                    return true;
                case FormulaChangeProperty.DISABLED:
                case FormulaChangeProperty.READONLY:
                case FormulaChangeProperty.HIDDEN:
                case FormulaChangeProperty.OPTIONS:
                case FormulaChangeProperty.SUPPORTED_VALUES:
                case FormulaChangeProperty.ACTIVE_INDEX:
                case FormulaChangeProperty.VALUE: {
                    this.handlePropertyChange(payload.id || id_1, payload.$kind, payload.value, immediatly);
                    return true;
                }
            }
        }
        return false;
    };
    FormContext.prototype.isElementValid = function (element) {
        var _this = this;
        return !findElementWith(function (it) { return !!_this.errors[it.id]; }, element);
    };
    FormContext.prototype.isFormValid = function () {
        return this.isElementValid(this.formDefinition);
    };
    // validate form
    FormContext.prototype.validate = function () {
        this.validator.validate(this.values);
    };
    FormContext.prototype.onElementDestroy = function (def) {
        this._definition$Map.delete(def.id);
    };
    FormContext.prototype.destroy = function () {
        this.destroy$.next();
        this.validator.destroy();
        this.clean();
    };
    FormContext.prototype.clean = function () {
        this.formDefinition = null;
        this.originDefinition = null;
        this.calulator.clean();
        this.validator.updateFrame(null);
    };
    FormContext.CHANGE_DEBOUNCE_TIME = 200;
    FormContext.CHANGE_IMMEDIATELY = Symbol('CHANGE_IMMEDIATELY');
    // async change key map/format
    FormContext.mapAsyncChangeKey = function (id, property) { return id + "." + property; };
    FormContext.formatAsyncChangeKey = function (key) { return key.split('.'); };
    return FormContext;
}());
export { FormContext };
//# sourceMappingURL=form-context.js.map